import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const api = {
  // Public endpoints
  getServices: () => axios.get(`${API}/services`),
  getService: (slug) => axios.get(`${API}/services/${slug}`),
  getTestimonials: () => axios.get(`${API}/testimonials`),
  getFaqs: () => axios.get(`${API}/faqs`),
  getGallery: () => axios.get(`${API}/gallery`),
  getSettings: () => axios.get(`${API}/settings`),
  getAvailability: (date) => axios.get(`${API}/availability?date=${date}`),
  getLandingPage: (slug) => axios.get(`${API}/landing-pages/${slug}`),
  
  // Booking
  createBooking: (data) => axios.post(`${API}/bookings`, data),
  
  // Contact
  submitContact: (data) => axios.post(`${API}/contact`, data),
  
  // Auth public
  setPassword: (token, password) => axios.post(`${API}/auth/set-password`, { token, password }),
  completeProfile: (data) => axios.put(`${API}/auth/complete-profile`, data, { headers: getAuthHeader(), withCredentials: true }),
  
  // Seed data
  seedData: () => axios.post(`${API}/seed`),
  
  // Admin endpoints
  admin: {
    // Services
    getServices: () => axios.get(`${API}/services`, { headers: getAuthHeader() }),
    createService: (data) => axios.post(`${API}/admin/services`, data, { headers: getAuthHeader() }),
    updateService: (id, data) => axios.put(`${API}/admin/services/${id}`, data, { headers: getAuthHeader() }),
    deleteService: (id) => axios.delete(`${API}/admin/services/${id}`, { headers: getAuthHeader() }),
    aiGenerateServiceSEO: (title, description, section) => axios.post(`${API}/admin/services/ai-generate`, { service_title: title, service_description: description, section }, { headers: getAuthHeader() }),
    
    // Testimonials
    getTestimonials: () => axios.get(`${API}/testimonials`, { headers: getAuthHeader() }),
    createTestimonial: (data) => axios.post(`${API}/admin/testimonials`, data, { headers: getAuthHeader() }),
    updateTestimonial: (id, data) => axios.put(`${API}/admin/testimonials/${id}`, data, { headers: getAuthHeader() }),
    deleteTestimonial: (id) => axios.delete(`${API}/admin/testimonials/${id}`, { headers: getAuthHeader() }),
    
    // FAQs
    getFaqs: () => axios.get(`${API}/faqs`, { headers: getAuthHeader() }),
    createFaq: (data) => axios.post(`${API}/admin/faqs`, data, { headers: getAuthHeader() }),
    updateFaq: (id, data) => axios.put(`${API}/admin/faqs/${id}`, data, { headers: getAuthHeader() }),
    deleteFaq: (id) => axios.delete(`${API}/admin/faqs/${id}`, { headers: getAuthHeader() }),
    
    // Gallery
    getGallery: () => axios.get(`${API}/gallery`, { headers: getAuthHeader() }),
    createGalleryItem: (data) => axios.post(`${API}/admin/gallery`, data, { headers: getAuthHeader() }),
    updateGalleryItem: (id, data) => axios.put(`${API}/admin/gallery/${id}`, data, { headers: getAuthHeader() }),
    deleteGalleryItem: (id) => axios.delete(`${API}/admin/gallery/${id}`, { headers: getAuthHeader() }),
    
    // Bookings
    getBookings: () => axios.get(`${API}/admin/bookings`, { headers: getAuthHeader() }),
    updateBookingStatus: (id, status) => axios.put(`${API}/admin/bookings/${id}/status?status=${status}`, null, { headers: getAuthHeader() }),
    deleteBooking: (id) => axios.delete(`${API}/admin/bookings/${id}`, { headers: getAuthHeader() }),
    
    // Customers
    getCustomers: () => axios.get(`${API}/admin/customers`, { headers: getAuthHeader() }),
    createCustomer: (data) => axios.post(`${API}/admin/customers`, data, { headers: getAuthHeader() }),
    updateCustomer: (id, data) => axios.put(`${API}/admin/customers/${id}`, data, { headers: getAuthHeader() }),
    deleteCustomer: (id) => axios.delete(`${API}/admin/customers/${id}`, { headers: getAuthHeader() }),
    
    // Settings
    getSettings: () => axios.get(`${API}/settings`, { headers: getAuthHeader() }),
    updateSettings: (data) => axios.put(`${API}/admin/settings`, data, { headers: getAuthHeader() }),
    getAvailabilitySettings: () => axios.get(`${API}/admin/availability-settings`, { headers: getAuthHeader() }),
    updateAvailabilitySettings: (data) => axios.put(`${API}/admin/availability-settings`, data, { headers: getAuthHeader() }),
    
    // Users
    getUsers: () => axios.get(`${API}/admin/users`, { headers: getAuthHeader() }),
    createUser: (data) => axios.post(`${API}/admin/users`, data, { headers: getAuthHeader() }),
    deleteUser: (id) => axios.delete(`${API}/admin/users/${id}`, { headers: getAuthHeader() }),
    
    // Contact Messages
    getContactMessages: () => axios.get(`${API}/admin/contact-messages`, { headers: getAuthHeader() }),
    markMessageRead: (id) => axios.put(`${API}/admin/contact-messages/${id}/read`, null, { headers: getAuthHeader() }),
    deleteMessage: (id) => axios.delete(`${API}/admin/contact-messages/${id}`, { headers: getAuthHeader() }),
    
    // Landing Pages
    getLandingPages: () => axios.get(`${API}/landing-pages`, { headers: getAuthHeader() }),
    createLandingPage: (data) => axios.post(`${API}/admin/landing-pages`, data, { headers: getAuthHeader() }),
    updateLandingPage: (id, data) => axios.put(`${API}/admin/landing-pages/${id}`, data, { headers: getAuthHeader() }),
    deleteLandingPage: (id) => axios.delete(`${API}/admin/landing-pages/${id}`, { headers: getAuthHeader() }),
    
    // Staff
    getStaff: () => axios.get(`${API}/admin/staff`, { headers: getAuthHeader() }),
    createStaff: (data) => axios.post(`${API}/admin/staff`, data, { headers: getAuthHeader() }),
    updateStaff: (id, data) => axios.put(`${API}/admin/staff/${id}`, data, { headers: getAuthHeader() }),
    deleteStaff: (id) => axios.delete(`${API}/admin/staff/${id}`, { headers: getAuthHeader() }),
    grantStaffAccess: (id) => axios.post(`${API}/admin/staff/${id}/grant-access`, null, { headers: getAuthHeader() }),
    revokeStaffAccess: (id) => axios.post(`${API}/admin/staff/${id}/revoke-access`, null, { headers: getAuthHeader() }),
    resetStaffPassword: (id) => axios.post(`${API}/admin/staff/${id}/reset-password`, null, { headers: getAuthHeader() }),
    assignEstimator: (bookingId, estimatorId) => axios.put(`${API}/admin/bookings/${bookingId}/assign?estimator_id=${estimatorId}`, null, { headers: getAuthHeader() }),
    getCalendar: (startDate, endDate) => axios.get(`${API}/admin/calendar?start_date=${startDate}&end_date=${endDate}`, { headers: getAuthHeader() }),
    
    // Jobs
    getJobs: () => axios.get(`${API}/admin/jobs`, { headers: getAuthHeader() }),
    createJob: (data) => axios.post(`${API}/admin/jobs`, data, { headers: getAuthHeader() }),
    createJobFromBooking: (bookingId) => axios.post(`${API}/admin/jobs/from-booking/${bookingId}`, null, { headers: getAuthHeader() }),
    updateJob: (id, data) => axios.put(`${API}/admin/jobs/${id}`, data, { headers: getAuthHeader() }),
    deleteJob: (id) => axios.delete(`${API}/admin/jobs/${id}`, { headers: getAuthHeader() }),
    exportJobsCsv: () => axios.get(`${API}/admin/jobs/export/csv`, { headers: getAuthHeader(), responseType: 'blob' }),
    getStaffMetrics: () => axios.get(`${API}/admin/staff-metrics`, { headers: getAuthHeader() }),
    getIntegrationStatus: () => axios.get(`${API}/admin/integrations/status`, { headers: getAuthHeader() }),
    sendEstimate: (jobId) => axios.post(`${API}/admin/send-estimate/${jobId}`, null, { headers: getAuthHeader() }),
    testEmail: () => axios.post(`${API}/admin/test-email`, null, { headers: getAuthHeader() }),
    generateInvoice: (jobId) => axios.post(`${API}/admin/jobs/${jobId}/generate-invoice`, null, { headers: getAuthHeader() }),
    sendInvoice: (jobId) => axios.post(`${API}/admin/jobs/${jobId}/send-invoice`, null, { headers: getAuthHeader() }),
    sendPortalLink: (jobId) => axios.post(`${API}/admin/jobs/${jobId}/send-portal-link`, null, { headers: getAuthHeader() }),
    rescheduleBooking: (bookingId, date, time, estimatorId) => axios.put(`${API}/admin/bookings/${bookingId}/reschedule?preferred_date=${date}&preferred_time=${time}${estimatorId ? '&assigned_estimator_id=' + estimatorId : ''}`, null, { headers: getAuthHeader() }),
    
    // File Uploads
    uploadPdf: (file) => {
      const formData = new FormData();
      formData.append('file', file);
      return axios.post(`${API}/admin/upload/pdf`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });
    },
    
    // Form Templates
    getFormTemplates: () => axios.get(`${API}/admin/form-templates`, { headers: getAuthHeader() }),
    createFormTemplate: (data) => axios.post(`${API}/admin/form-templates`, data, { headers: getAuthHeader() }),
    updateFormTemplate: (id, data) => axios.put(`${API}/admin/form-templates/${id}`, data, { headers: getAuthHeader() }),
    deleteFormTemplate: (id) => axios.delete(`${API}/admin/form-templates/${id}`, { headers: getAuthHeader() }),
    
    // Form Submissions (Client Documents)
    getFormSubmissions: (clientId, formTemplateId) => {
      let url = `${API}/admin/form-submissions`;
      const params = [];
      if (clientId) params.push(`client_id=${clientId}`);
      if (formTemplateId) params.push(`form_template_id=${formTemplateId}`);
      if (params.length) url += '?' + params.join('&');
      return axios.get(url, { headers: getAuthHeader() });
    },
    createFormSubmission: (formTemplateId, clientId, data = {}, jobId = null) => 
      axios.post(`${API}/admin/form-submissions?form_template_id=${formTemplateId}&client_id=${clientId}${jobId ? '&job_id=' + jobId : ''}`, data, { headers: getAuthHeader() }),
    updateFormSubmission: (id, data, status = null, signatureData = null) => {
      let url = `${API}/admin/form-submissions/${id}`;
      const params = [];
      if (status) params.push(`status=${status}`);
      if (signatureData) params.push(`signature_data=${encodeURIComponent(signatureData)}`);
      if (params.length) url += '?' + params.join('&');
      return axios.put(url, data, { headers: getAuthHeader() });
    },
    deleteFormSubmission: (id) => axios.delete(`${API}/admin/form-submissions/${id}`, { headers: getAuthHeader() }),
    
    // Spreadsheet Views
    getSpreadsheetByForm: (formTemplateId) => axios.get(`${API}/admin/forms/spreadsheet/by-form/${formTemplateId}`, { headers: getAuthHeader() }),
    getSpreadsheetByClient: (clientId) => axios.get(`${API}/admin/forms/spreadsheet/by-client/${clientId}`, { headers: getAuthHeader() }),
    exportFormCsv: (formTemplateId) => axios.get(`${API}/admin/forms/spreadsheet/export/${formTemplateId}`, { headers: getAuthHeader(), responseType: 'blob' }),
    initClientFolder: (clientId) => axios.post(`${API}/admin/forms/init-client-folder/${clientId}`, null, { headers: getAuthHeader() }),

    // Customer Reachout
    getReachoutSettings: () => axios.get(`${API}/admin/reachout/settings`, { headers: getAuthHeader() }),
    updateReachoutSettings: (data) => axios.put(`${API}/admin/reachout/settings`, data, { headers: getAuthHeader() }),
    getReachoutCampaigns: () => axios.get(`${API}/admin/reachout/campaigns`, { headers: getAuthHeader() }),
    getReachoutCampaign: (id) => axios.get(`${API}/admin/reachout/campaigns/${id}`, { headers: getAuthHeader() }),
    createReachoutCampaign: (data) => axios.post(`${API}/admin/reachout/campaigns`, data, { headers: getAuthHeader() }),
    updateReachoutCampaign: (id, data) => axios.put(`${API}/admin/reachout/campaigns/${id}`, data, { headers: getAuthHeader() }),
    deleteReachoutCampaign: (id) => axios.delete(`${API}/admin/reachout/campaigns/${id}`, { headers: getAuthHeader() }),
    generateReachoutCampaign: (type) => axios.post(`${API}/admin/reachout/campaigns/generate?campaign_type=${type}`, null, { headers: getAuthHeader() }),
    sendReachoutCampaign: (id) => axios.post(`${API}/admin/reachout/campaigns/${id}/send`, null, { headers: getAuthHeader() }),
    getReachoutCampaignLogs: (id) => axios.get(`${API}/admin/reachout/campaigns/${id}/logs`, { headers: getAuthHeader() }),
    getReachoutHistory: (customerId) => axios.get(`${API}/admin/reachout/history${customerId ? '?customer_id=' + customerId : ''}`, { headers: getAuthHeader() }),

    // Leads
    getLeads: () => axios.get(`${API}/admin/leads`, { headers: getAuthHeader() }),
    updateLead: (id, data) => axios.put(`${API}/admin/leads/${id}`, data, { headers: getAuthHeader() }),
    deleteLead: (id) => axios.delete(`${API}/admin/leads/${id}`, { headers: getAuthHeader() }),

    // Blog Schedule
    getBlogSchedule: () => axios.get(`${API}/admin/blog/schedule`, { headers: getAuthHeader() }),
  },
  // Public Portal
  portal: {
    getJob: (jobId) => axios.get(`${API}/portal/job/${jobId}`),
    approveJob: (jobId) => axios.post(`${API}/portal/job/${jobId}/approve`)
  }
};

export default api;
